import { channelConfig } from '@finn-no/adboost/channels';
import { Children, useEffect, useRef, useState } from 'react';
import { BlinkDot } from '~/components/BlinkDot';
import BlinkLogo from '~/components/icons/blinkLogo';
import { Business07, Realestate27, Things10, Things39 } from '~/components/icons/Illustrations';

export function Timelines() {
    const ref = useRef<HTMLDivElement>(null);
    const [tab, setTab] = useState(1);

    // sjekk om komponenten er synlig eller ikke
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!isVisible && entry.isIntersecting) {
                    setIsVisible(entry.isIntersecting);
                }
            },
            { rootMargin: '0px', threshold: 0.2 },
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, []);

    return (
        <div
            ref={ref}
            style={{
                minHeight: '2000px',
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
            }}>
            <div className="relative mb-144 flex w-full flex-row justify-center overflow-hidden">
                <div
                    style={{ padding: '16px' }}
                    className={`p-16 ${tab == 1 ? 'text-green-300' : 'cursor-pointer'}`}
                    onClick={() => setTab(1)}>
                    <p>Automatisk bestilling</p>
                    {tab == 1 && (
                        <div
                            className="w-full rounded-full bg-green-300"
                            style={{
                                height: '4px',
                            }}
                        />
                    )}
                </div>
                <div
                    style={{ padding: '16px' }}
                    className={`p-16 ${tab == 2 ? 'text-green-300' : 'cursor-pointer'}`}
                    onClick={() => setTab(2)}>
                    <p>Manuell bestilling</p>
                    {tab == 2 && (
                        <div
                            className="w-full rounded-full bg-green-300"
                            style={{
                                height: '4px',
                            }}
                        />
                    )}
                </div>
            </div>

            {/* Automatisk bestilling */}
            {isVisible && tab === 1 && (
                <div>
                    <TimelineBranch>
                        <div className="flex w-full flex-row justify-center">
                            <div className="bg-blue-alt-800 max-w-max rounded-full px-24 pb-16 pt-24 md:px-32 md:pb-24 md:pt-32 z-10">
                                <h3 className="text-16 font-bold text-green-100 md:text-22">Boligen publiseres på FINN.no</h3>
                            </div>
                        </div>

                        <TimelineEvent reverse={false}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexFlow: 'row',
                                    alignItems: 'center',
                                }}>
                                <div>
                                    <div className="h-128"></div>
                                    <div className="nomobile text-green-300" style={{ fontSize: '82pt', paddingRight: '32px' }}>
                                        1
                                    </div>
                                </div>
                                <div className="eventContent">
                                    <Things39 width={140} height={105} />
                                    <h3 className="text-green-300">Automatisk bestilling</h3>
                                    Blink-annonsene lages automatisk når boligen blir publisert på FINN.no, hvis man har avtale med
                                    automatisk bestilling. Enkelt og greit for både megler og boligselger!
                                </div>
                            </div>
                        </TimelineEvent>

                        <FactBox pos="justify-end">
                            <div>
                                <h3 className="text-green-300">Lett som en plett</h3>
                                Megler trenger nemlig ikke gjøre noe mer enn å klikke på en knapp, og så fikses alt fra distribusjon,
                                målretting og materiell. Teknologien bak Blink finner riktig kanalmiks for å nå målgruppen, og materiell som
                                bilder, overskrift og visningsdato hentes fra FINN-annonsen din.
                            </div>
                        </FactBox>

                        <TimelineEvent reverse={true}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexFlow: 'row',
                                    alignItems: 'center',
                                }}>
                                <div>
                                    <div className="h-128"></div>
                                    <div
                                        className="nomobile text-green-300"
                                        style={{
                                            fontSize: '82pt',
                                            paddingLeft: '12px',
                                            paddingRight: '32px',
                                        }}>
                                        2
                                    </div>
                                </div>
                                <div className="eventContent">
                                    <Things10 width={196} height={122} />
                                    <h3 className="text-green-300">Kampanjen aktiveres</h3>
                                    Etter bestilling går resten av seg selv, og du kan følge med på kampanjen underveis.
                                </div>
                            </div>
                        </TimelineEvent>

                        <FactBox pos="justify-start">
                            <h3 className="text-green-300">90 % av Norge på én uke</h3>
                            <div>
                                Schibsteds kanaler når ut til hele 90 % av Norges befolkning på én uke. Med Blink blir boligannonsen din
                                vist på nettsteder som VG, Aftenposten, Bergens Tidende og en hel rekke lokalaviser, i tillegg til Facebook
                                og Instagram. Her er det med andre ord store sjanser for å finne boligkjøperen du leter etter.
                            </div>
                            <div
                                className="nomobile"
                                style={{
                                    position: 'absolute',
                                    left: '150%',
                                    top: 0,
                                    width: '650px',
                                }}>
                                <ChannelLogoCarousel />
                            </div>
                        </FactBox>

                        <TimelineEvent reverse={false}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexFlow: 'row',
                                    alignItems: 'center',
                                }}>
                                <div>
                                    <div className="h-128"></div>
                                    <div
                                        className="nomobile text-green-300"
                                        style={{
                                            fontSize: '82pt',
                                            paddingLeft: '32px',
                                            paddingRight: '32px',
                                        }}>
                                        3
                                    </div>
                                </div>
                                <div className="eventContent">
                                    <div style={{ paddingBottom: '12px' }}>
                                        <Business07 width={105} height={101} />
                                    </div>
                                    <h3 className="text-green-300">Følg med underveis</h3>
                                    Både megler og boligselger kan følge med på kampanjen underveis.
                                </div>
                            </div>
                        </TimelineEvent>

                        <FactBox pos="justify-end">
                            <div>
                                <h3 className="text-green-300">Landets beste målgruppe?</h3>
                                Helt unik data er det som gjør Blink magisk. Vi vet hvem som ser på boligannonser på FINN i dag, og hvilke
                                boliger og områder de ser på. Dette bruker vi til å bygge målgrupper med personer som er interessert i
                                akkurat din type bolig. Og det smarteste av alt; vi vet hvem i målgruppen som ikke har sett annonsen din
                                enda. Da kan du føle deg trygg på at annonsen din blir sett av enda flere og av en skreddersydd målgruppe.
                            </div>
                        </FactBox>

                        <FactBox pos="justify-start">
                            <div>
                                <h3 className="text-green-300">Se hvordan kampanjen leverer</h3>
                                Siden Blink er tett knyttet til boligannonsen din finner du alt alt av tall og informasjon om hvordan en
                                Blink kampanje leverer i FINN-statistikken og både megler og boligselger får et komplett bilde.
                            </div>
                        </FactBox>

                        <TimelineEvent reverse={true}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexFlow: 'row',
                                    alignItems: 'center',
                                }}>
                                <div>
                                    <div className="h-128"></div>
                                    <div
                                        className="nomobile text-green-300"
                                        style={{
                                            fontSize: '82pt',
                                            paddingLeft: '12px',
                                            paddingRight: '32px',
                                        }}>
                                        4
                                    </div>
                                </div>
                                <div className="eventContent">
                                    <Realestate27 width={172} height={128} />
                                    <h3 className="text-green-300">Visning</h3>
                                    Blink-kampanjen avsluttes etter syv dager.
                                </div>
                            </div>
                        </TimelineEvent>

                        <FactBox>
                            <h3 className="text-green-300">30% flere besøkende</h3>
                            <div>
                                Om det lønner seg å bruke Blink, spør du? Ja, det kan vi virkelig påstå at det gjør. Våre tall viser at
                                annonser med Blink får ca. 30 % flere besøkende enn annonser uten. Det kan jo være nesten 30 % flere
                                potensielle boligkjøpere, det!
                            </div>
                        </FactBox>

                        <div className="relative flex w-full justify-center">
                            <div className="bg-blue-alt-800 max-w-max rounded-full px-24 pb-16 pt-24 md:px-32 md:pb-24 md:pt-32">
                                <h3 className="text-16 font-bold text-green-100 md:text-22">Solgt 👏</h3>
                            </div>
                        </div>
                    </TimelineBranch>
                </div>
            )}

            {/* Manuell bestilling */}
            {isVisible && tab === 2 && (
                <div>
                    <TimelineBranch>
                        <div className="flex w-full flex-row justify-center">
                            <div className="bg-blue-alt-800 max-w-max rounded-full px-24 pb-16 pt-24 md:px-32 md:pb-24 md:pt-32 z-10">
                                <h3 className="text-16 font-bold text-green-100 md:text-22 ">Boligen publiseres på FINN.no</h3>
                            </div>
                        </div>

                        <TimelineEvent reverse={false}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexFlow: 'row',
                                    alignItems: 'center',
                                }}>
                                <div>
                                    <div className="h-128"></div>
                                    <div className="nomobile text-green-300" style={{ fontSize: '82pt', paddingRight: '32px' }}>
                                        1
                                    </div>
                                </div>
                                <div className="eventContent">
                                    <Things39 width={140} height={105} />
                                    <h3 className="text-green-300">Manuell bestilling</h3>
                                    Bestilles manuelt for en enkeltbolig i FINN Admin. Dato for aktivering bestemmer du selv. Hvis du er
                                    boligselger kan du spørre megleren din om å bestille Blink.
                                </div>
                            </div>
                        </TimelineEvent>

                        <FactBox pos="justify-end">
                            <div>
                                <h3 className="text-green-300">Lett som en plett</h3>
                                Megler trenger nemlig ikke gjøre noe mer enn å klikke på en knapp, og så fikses alt fra distribusjon,
                                målretting og materiell. Teknologien bak Blink finner riktig kanalmiks for å nå målgruppen, og materiell som
                                bilder, overskrift og visningsdato hentes fra FINN-annonsen din.
                            </div>
                        </FactBox>

                        <TimelineEvent reverse={true}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexFlow: 'row',
                                    alignItems: 'center',
                                }}>
                                <div>
                                    <div className="h-128"></div>
                                    <div
                                        className="nomobile text-green-300"
                                        style={{
                                            fontSize: '82pt',
                                            paddingLeft: '32px',
                                            paddingRight: '32px',
                                        }}>
                                        2
                                    </div>
                                </div>
                                <div className="eventContent">
                                    <Things10 width={196} height={122} />
                                    <h3 className="text-green-300">Kampanjen aktiveres</h3>
                                    Etter bestilling går resten av seg selv, og du kan følge med på kampanjen underveis.
                                </div>
                            </div>
                        </TimelineEvent>

                        <FactBox pos="justify-start">
                            <h3 className="text-green-300">90 % av Norge på én uke</h3>
                            <div>
                                Schibsteds kanaler når ut til hele 90 % av Norges befolkning på én uke. Med Blink blir boligannonsen din
                                vist på nettsteder som VG, Aftenposten, Bergens Tidende og en hel rekke lokalaviser, i tillegg til Facebook
                                og Instagram. Her er det med andre ord store sjanser for å finne boligkjøperen du leter etter.
                            </div>
                            <div
                                className="nomobile"
                                style={{
                                    position: 'absolute',
                                    left: '150%',
                                    top: 0,
                                    width: '650px',
                                }}>
                                <ChannelLogoCarousel />
                            </div>
                        </FactBox>

                        <TimelineEvent reverse={false}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexFlow: 'row',
                                    alignItems: 'center',
                                }}>
                                <div>
                                    <div className="h-128"></div>
                                    <div
                                        className="nomobile text-green-300"
                                        style={{
                                            fontSize: '82pt',
                                            paddingLeft: '32px',
                                            paddingRight: '32px',
                                            paddingBottom: '14px',
                                        }}>
                                        3
                                    </div>
                                </div>
                                <div className="eventContent">
                                    <div style={{ paddingBottom: '12px' }}>
                                        <Business07 width={105} height={101} />
                                    </div>
                                    <h3 className="text-green-300">Følg med underveis</h3>
                                    BLINK aktiveres på valgt dato. Kampanjen varer i 7 dager.
                                </div>
                            </div>
                        </TimelineEvent>

                        <FactBox pos="justify-end">
                            <div>
                                <h3 className="text-green-300">Landets beste målgruppe?</h3>
                                Helt unik data er det som gjør Blink magisk. Vi vet hvem som ser på boligannonser på FINN i dag, og hvilke
                                boliger og områder de ser på. Dette bruker vi til å bygge målgrupper med personer som er interessert i
                                akkurat din type bolig. Og det smarteste av alt; vi vet hvem i målgruppen som ikke har sett annonsen din
                                enda. Da kan du føle deg trygg på at annonsen din blir sett av enda flere og av en skreddersydd målgruppe.
                            </div>
                        </FactBox>

                        <FactBox pos="justify-start">
                            <div>
                                <h3 className="text-green-300">Se hvordan kampanjen leverer</h3>
                                Siden Blink er tett knyttet til boligannonsen din finner du alt alt av tall og informasjon om hvordan en
                                Blink kampanje leverer i FINN-statistikken og både megler og boligselger får et komplett bilde.
                            </div>
                        </FactBox>

                        <TimelineEvent reverse={true}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexFlow: 'row',
                                    alignItems: 'center',
                                }}>
                                <div>
                                    <div className="h-128"></div>
                                    <div
                                        className="nomobile text-green-300"
                                        style={{
                                            fontSize: '82pt',
                                            paddingLeft: '32px',
                                            paddingRight: '32px',
                                        }}>
                                        4
                                    </div>
                                </div>
                                <div className="eventContent">
                                    <Realestate27 width={172} height={128} />
                                    <h3 className="text-green-300">Visning</h3>
                                    Blink-kampanjen avsluttes etter syv dager.
                                </div>
                            </div>
                        </TimelineEvent>

                        <FactBox pos="justify-start">
                            <h3 className="text-green-300">30% flere besøkende</h3>
                            <div>
                                Om det lønner seg å bruke Blink, spør du? Ja, det kan vi virkelig påstå at det gjør. Våre tall viser at
                                annonser med Blink får ca. 30 % flere besøkende enn annonser uten. Det kan jo være nesten 30 % flere
                                potensielle boligkjøpere, det!
                            </div>
                        </FactBox>

                        <div className="relative flex w-full justify-center">
                            <div className="bg-blue-alt-800 max-w-max rounded-full px-24 pb-16 pt-24 md:px-32 md:pb-24 md:pt-32">
                                <h3 className="text-16 font-bold text-green-100 md:text-22">Solgt 👏</h3>
                            </div>
                        </div>
                    </TimelineBranch>
                </div>
            )}
        </div>
    );
}

function TimelineBranch({ children }: { children: JSX.Element[] | JSX.Element }) {
    const mappedChildren = Children.map(children, (child, index) => {
        return (
            <TimelineItem totalChildren={Array.isArray(children) ? children.length : 1} index={index}>
                {child}
            </TimelineItem>
        );
    });
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                flexFlow: 'column',
            }}>
            {mappedChildren}
        </div>
    );
}

function TimelineItem({ index, children, totalChildren }: { children: JSX.Element[] | JSX.Element; index: number; totalChildren: number }) {
    const [show, setShow] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setShow(true);
        }, index * 600);

        return () => clearTimeout(timeout);
    }, [show]);

    if (!show) return <div></div>;

    return (
        <div
            className={`progress-bar-wrapper${index === 0 ? '-first' : totalChildren === index + 1 ? '-last' : ''}`}
            style={{
                position: 'relative',
                margin: 0,
                maxWidth: '1252px',
                width: '100%',
                display: 'block',
            }}>
            {/*the progress-bar line*/}
            <div
                style={{
                    position: 'absolute',
                    left: '0',
                    right: 0,
                    margin: 'auto',
                    top: '0px',
                    zIndex: 0,
                    width: '4px',
                    height: `100%`,
                    animationDelay: '0',
                    animationDuration: '600ms',
                    animationName: 'scaleTimeLine',
                    animationFillMode: 'forwards',
                    animationTimingFunction: 'linear',
                    backgroundColor: '#253652',
                    opacity: 0,
                }}></div>
            {/* the content */}
            <div
                id={`${index}item`}
                style={{
                    animationDelay: '100ms',
                    animationDuration: '500ms',
                    animationName: 'timelineItemAppears',
                    animationFillMode: 'forwards',
                    opacity: 0,
                    zIndex: 10,
                }}>
                {children}
            </div>
        </div>
    );
}

function TimelineEvent({ children, reverse = false }: { children: JSX.Element[] | JSX.Element; reverse?: boolean }) {
    return (
        <div
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: reverse ? 'row' : 'row-reverse',
                alignItems: 'center',
                justifyContent: reverse ? 'end' : 'start',
                // justifyContent:"space-between"
            }}>
            <div className="nomobile" style={{ flexBasis: '4%' }}>
                <EventCircle />
            </div>
            <div className="nomobile eventContentBlock" style={{ flexBasis: '48%' }}>
                <EventContent reverse={reverse}>{children}</EventContent>
            </div>

            <div className="onlymobile" style={{ width: '100%' }}>
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                        flexFlow: 'row',
                        justifyContent: 'center',
                    }}>
                    <div className="eventContentBlock">{children}</div>
                </div>
            </div>
        </div>
    );
}

function EventContent({ children, reverse }: { reverse: boolean; children: JSX.Element[] | JSX.Element }) {
    if (children === null) {
        return <div></div>;
    }

    let ref = useRef<HTMLDivElement>(null);
    // sjekk om komponenten er synlig eller ikke
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!isVisible && entry.isIntersecting) {
                    setIsVisible(entry.isIntersecting);
                }
            },
            { rootMargin: '0px', threshold: 0.1 },
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, []);

    const flexDirection = reverse ? 'row-reverse' : 'row';
    return (
        <div
            ref={ref}
            style={{
                display: 'flex',
                flexDirection: flexDirection,
                alignItems: 'center',
                overflow: 'hidden',
            }}>
            <div
                style={{
                    display: 'block',
                    flexBasis: '60%',
                    textAlign: 'left',
                }}
                className="text-green-100">
                {children}
            </div>
            <div
                className="nomobile"
                style={{
                    flexBasis: '40%',
                    display: 'block',
                }}>
                <div
                    className={isVisible ? 'slideCard' : reverse ? 'slideCard slideCardInactiveR' : 'slideCard slideCardInactiveL'}
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: flexDirection,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <BlinkDot height={22} width={22} backgroundColor={'green-100'} />
                    <div
                        className="bg-green-300"
                        style={{
                            maxWidth: '168px',
                            height: '4px',
                            flexBasis: '70%',
                            marginLeft: reverse ? '8px' : '16px',
                            marginRight: reverse ? '8px' : '8px',
                            borderRadius: '2px',
                        }}></div>
                </div>
            </div>
        </div>
    );
}

function EventCircle({ children }: { children?: JSX.Element[] | JSX.Element }) {
    return (
        <div
            style={{
                height: '44px',
                width: '44px',
                display: 'inline-block',
                borderRadius: '22px',
                borderWidth: '4px',
                backgroundColor: '#191D3A',
                borderColor: '#67EEB8',
                animationDuration: '800ms',
                animationName: 'eventCircleAppears',
                animationFillMode: 'forwards',
                margin: 0,
                padding: '6px',
                fontSize: '18pt',
            }}>
            {children}
        </div>
    );
}

function FactBox({ children, pos = 'justify-end' }: { children: JSX.Element[] | JSX.Element; pos?: string }) {
    const [plusRotation, setPlusRotation] = useState('rotate(0deg)');
    const [expanded, setExpanded] = useState(false);
    const [boxOpacity, setBoxOpacity] = useState('0.0');

    function expand() {
        setExpanded(!expanded);
        setBoxOpacity(!expanded ? '1.0' : '0.0');
        setPlusRotation(!expanded ? 'rotate(225deg)' : 'rotate(0deg)');
    }

    const classes = ['factboxWrapper', pos];

    return (
        <div className={classes.join(' ')} onClick={() => expand()}>
            <div className="factboxCircle">
                <FactboxCircle>
                    <div style={{ transform: plusRotation, transitionDuration: '300ms' }}>+</div>
                </FactboxCircle>
            </div>
            <div className="factbox nomobile" style={{ opacity: boxOpacity }}>
                <div className="factboxInfo">i</div>
                {children}
            </div>
            <div className="onlymobile">
                <div className="factbox " style={{ display: expanded ? 'block' : 'none', opacity: boxOpacity }}>
                    <div className="factboxInfo">i</div>
                    {children}
                </div>
            </div>
        </div>
    );
}

function FactboxCircle({ children }: { children: JSX.Element[] | JSX.Element }) {
    return (
        <div
            className="cursor-pointer"
            style={{
                height: '44px',
                width: '44px',
                display: 'inline-block',
                borderRadius: '22px',
                backgroundColor: '#253652',
                animationDuration: '800ms',
                animationName: 'eventCircleAppears',
                animationFillMode: 'forwards',
                margin: 0,
                padding: '9px',
                fontWeight: 'bold',
                color: '#67EEB8',
                fontSize: '24pt',
            }}>
            {children}
        </div>
    );
}

function ChannelLogoCarousel() {
    return (
        <div>
            <div>
                {channels.map((channel, index) => (
                    <ChannelLogo key={channel} channel={channel} index={index} />
                ))}
            </div>
        </div>
    );
}

function ChannelLogo({ channel, index }: { channel: string; index: number }) {
    let Logo = channelConfig(channel).logo;
    let radius = 21;

    if (Logo) {
        return (
            <div
                style={{
                    display: 'inline-block',
                    margin: '8px',
                }}
                title={channel}>
                <Logo id="bubble" width={radius * 2} height={radius * 2} x={0} y={0}></Logo>
            </div>
        );
    } else {
        return <div>{channel}</div>;
    }
}

export function Sign() {
    return (
        <div className="sign">
            <div className="back">
                <div className="text-blue-alt-900 m-auto flex w-full items-center justify-center text-34">Visning</div>
            </div>
            <div className="front">
                <BlinkLogo width={80} height={80} />
            </div>
        </div>
    );
}

const channels = [
    'adressa.no',
    'aftenbladet.no',
    'altaposten',
    'andalsnes-avis',
    'aftenposten.no',
    'avisast',
    'banett',
    'bladet',
    'bt.no',
    'dolen',
    'driva',
    'e24',
    'facebook',
    'instagram',
    'finn-resultpage',
    'fjordingen',
    'fjordabladet',
    'fjt',
    'fjuken',
    'folkebladet',
    'fosnafolket',
    'framtidinord',
    'firdatidend',
    'fvn.no',
    'hitrafroya',
    'ht',
    'innherred',
    'itromso',
    'morenytt',
    'opdalingen',
    'rbnett',
    'smp',
    'nyss',
    'tek',
    'tronderbladet',
    'vestlandsnytt',
    'vigga',
    'vikebladet',
    'vg.no',
    'vol',
];
